import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpFromLine, Briefcase, ChevronRight, EyeOff, FlipVertical2, Heart, PenLine, Sparkles } from "lucide-react";
import Seo from "../components/seo";
import cardData from "../components/card-data";
import { cardPath, isLegacyCardSlug, resolveCardBySlug } from "../utils/cardRoutes";
import {
  COMMON_COPY_ES,
  isSpanishLocale,
  localizedPath,
  spanishCardOverview,
  spanishCardReading,
  spanishJournalPrompts,
  spanishLifeMeaning,
  spanishMeaningLines,
  spanishShadowMessage,
  translateCardAlt,
  translateCardName,
  translateEmotionLabel,
  translateKeywordPhrase,
  translateSuitName,
  translateSymbolLabel,
  translateSymbolMeaning,
} from "../utils/i18n";

/* =========================
   Background & Theme (more turquoise)
========================= */
const CARD_BG = `
 radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;
/* Distinct frosted cards */
const BOX_BASE =
  "p-4 sm:p-5 border backdrop-blur-md relative overflow-hidden";

const UPRIGHT_BG = "bg-gradient-to-b from-[#03779B]/70 via-[#0C4B84]/65 to-[#005C83]/20";
const REVERSED_BG = "bg-[#821E3C]/75";
const MEANING_HEADER =
  "flex items-center gap-2 border-b border-white/20 pb-2 sm:pb-2.5";
const MEANING_ICON =
  "h-[17px] w-[17px] shrink-0 stroke-[2]";
const MEANING_LIST =
  "mt-3 space-y-1.5 text-[16px] leading-[1.55] sm:mt-4 sm:space-y-2 sm:leading-[1.6]";
const MEANING_ITEM =
  "relative pl-4 text-white/90 before:absolute before:left-0 before:top-[0.68em] before:h-1.5 before:w-1.5 before:rounded-full before:bg-white/45";
const MEANING_LABEL =
  "font-semibold text-white";
const SECTION_TITLE =
  "text-xl font-semibold leading-tight tracking-[0.08em] text-white";
const PANEL_TITLE =
  "text-[17px] md:text-[18px] font-semibold leading-tight tracking-[0.08em] text-white";
const CONTENT_TEXT =
  "text-[16.5px] leading-[1.65] sm:text-[17px] sm:leading-[1.75] text-white/90";

/* Site-wide SEO constants (match About.js) */
const siteUrl = "https://tarotfate.com";
const logoWebp = `${siteUrl}/icons/logo.webp`;     // organization logo

const splitMeaningText = (item = "") => {
  const text = String(item).replace(/\s+/g, " ").trim();
  const colonIndex = text.indexOf(":");

  if (colonIndex > -1) {
    return [
      text.slice(0, colonIndex).trim() || "Meaning",
      text.slice(colonIndex + 1).trim(),
    ];
  }

  const dashMatch = text.match(/^(.+?)\s[-\u2013\u2014]\s(.+)$/);
  if (dashMatch) return [dashMatch[1].trim() || "Meaning", dashMatch[2].trim()];

  return ["Meaning", text];
};

const normalizeFaq = (item) => {
  if (!item) return null;
  if (typeof item === "string") return null;
  const question = item.question || item.q || item.name;
  const answer = item.answer || item.a || item.text;
  if (!question || !answer) return null;
  return { question, answer };
};

export default function TarotCardPage() {
  const { cardSlug } = useParams();
  const card = resolveCardBySlug(cardData, cardSlug);
  const isSpanish = isSpanishLocale();
  const copy = isSpanish ? COMMON_COPY_ES : null;

  const suitToPath = {
    "Major Arcana": "/tarot/major-arcana",
    Wands: "/tarot/wands",
    Cups: "/tarot/cups",
    Swords: "/tarot/swords",
    Pentacles: "/tarot/pentacles",
  };

  if (!card) {
    return (
      <div className="min-h-screen grid place-items-center" style={{ background: CARD_BG }}>
        <div className="text-center p-10 rounded-2xl bg-white/10 backdrop-blur-lg border border-white/15 text-white">
          <h2 className="text-xl font-semibold">{copy?.tarotCardNotFound || "Tarot card not found"}</h2>
          <p className="mt-2 text-[16px] text-white/80">{copy?.checkUrl || "Check the URL or browse all cards."}</p>
          <Link to={localizedPath("/tarot-cards")} className="underline">{copy?.allTarotCards || "All Tarot Cards"}</Link>
        </div>
      </div>
    );
  }

  if (isLegacyCardSlug(card, cardSlug)) {
    return <Navigate to={localizedPath(cardPath(card))} replace />;
  }

  /* =========================
     SEO helpers
  ========================= */
  const pagePath = localizedPath(cardPath(card));
  const pageUrl = `${siteUrl}${pagePath}`;
  const cardImg = card.image || `${siteUrl}/card/${card.slug}.webp`;
  const absoluteCardImg = cardImg.startsWith("http") ? cardImg : `${siteUrl}${cardImg}`;

  const splitFirstMeaning = (arr = []) => {
    if (!arr?.length) return ["Meaning", ""];
    return splitMeaningText(arr[0]);
  };
  const uprightMeanings = isSpanish ? spanishMeaningLines(card, "upright") : card.upright || [];
  const reversedMeanings = isSpanish ? spanishMeaningLines(card, "reversed") : card.reversed || [];
  const [, uprightDesc0] = splitFirstMeaning(uprightMeanings);
  const [, reversedDesc0] = splitFirstMeaning(reversedMeanings);

  const cardName = translateCardName(card.name);
  const cardSuit = translateSuitName(card.suit);
  const cardSubtitle = translateKeywordPhrase(card.subtitle);
  const pageTitle =
    isSpanish
      ? `${cardName} | Significado al derecho, invertido y consejo | TarotFate`
      : card.seoTitle || `${card.name} | Upright, Reversed and Advice | TarotFate`;
  const pageHeading = isSpanish ? cardName : card.h1 || card.name;
  const displayPageHeading = pageHeading.toUpperCase();
  const imageAlt = isSpanish
    ? `${translateCardAlt(card.name)} con significado e interpretación`
    : card.imageAlt || `${card.name} tarot card artwork and meaning`;
  const metaDescription =
    isSpanish
      ? spanishCardOverview(card, uprightDesc0, reversedDesc0)
      : card.metaDescription ||
        [
          card.subtitle,
          uprightDesc0 && `Upright: ${uprightDesc0}`,
          reversedDesc0 && `Reversed: ${reversedDesc0}`,
        ]
          .filter(Boolean)
          .join(" ");

  const pageFaqs = isSpanish
    ? []
    : [...(card.commonQuestions || [])]
        .map(normalizeFaq)
        .filter(Boolean);
  const relatedCardSlugs = card.relatedCards?.length ? card.relatedCards : card.mirroring || [];
  const hasReflectionPair = Boolean(card.shadowMessage && card.journalPrompts?.length > 0);
  const journalPrompts = isSpanish ? spanishJournalPrompts(card) : card.journalPrompts || [];
  const localizedSymbols = (card.symbols || []).map((symbol) => {
    const label = typeof symbol === "string" ? symbol : symbol.label;
    const meaning = typeof symbol === "string" ? "" : symbol.meaning;
    return {
      label: isSpanish ? translateSymbolLabel(label) : label,
      meaning: isSpanish ? translateSymbolMeaning(label, meaning) : meaning,
    };
  });
  const suitNavItems = [
    { label: translateSuitName("Major Arcana"), path: localizedPath(suitToPath["Major Arcana"]), active: card.suit === "Major Arcana" },
    { label: translateSuitName("Wands"), path: localizedPath(suitToPath.Wands), active: card.suit === "Wands" },
    { label: translateSuitName("Cups"), path: localizedPath(suitToPath.Cups), active: card.suit === "Cups" },
    { label: translateSuitName("Swords"), path: localizedPath(suitToPath.Swords), active: card.suit === "Swords" },
    { label: translateSuitName("Pentacles"), path: localizedPath(suitToPath.Pentacles), active: card.suit === "Pentacles" },
    { label: copy?.allCards || "All Cards", path: localizedPath("/tarot-cards") },
  ];

  const schemaKeywords = [
    cardName,
    isSpanish ? `${cardName} significado al derecho` : `${card.name} upright meaning`,
    isSpanish ? `${cardName} significado invertido` : `${card.name} reversed meaning`,
    card.suit && (isSpanish ? `${cardSuit} tarot` : `${card.suit} tarot cards`),
    isSpanish ? `${cardName} significado en el amor` : `${card.name} love meaning`,
    isSpanish ? `${cardName} significado en el trabajo` : `${card.name} career meaning`,
  ]
    .filter(Boolean)
    .join(", ");


  // JSON-LD graph (About.js style: logo.webp + og-cover.jpg)
  const schemaGraph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: {
          "@type": "ImageObject",
          url: logoWebp,
          width: 512,
          height: 512
        }
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: "en"
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: pageTitle,
        description: metaDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@id": `${pageUrl}#image` },
        inLanguage: isSpanish ? "es" : "en",
        breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
        mainEntity: { "@id": `${pageUrl}#card` },
        relatedLink: relatedCardSlugs.map((slug) => `${siteUrl}${localizedPath(cardPath(slug, cardData))}`)
      },
      {
        "@type": "ImageObject",
        "@id": `${pageUrl}#image`,
        url: absoluteCardImg,
        caption: imageAlt
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: isSpanish ? "Inicio" : "Home", item: isSpanish ? `${siteUrl}/es` : siteUrl },
          { "@type": "ListItem", position: 2, name: copy?.allTarotCards || "Tarot Cards", item: `${siteUrl}${localizedPath("/tarot-cards")}` },
          ...(card.suit
            ? [
                {
                  "@type": "ListItem",
                  position: 3,
                  name: cardSuit,
                  item: `${siteUrl}${localizedPath(suitToPath[card.suit])}`
                }
              ]
            : []),
          { "@type": "ListItem", position: card.suit ? 4 : 3, name: cardName, item: pageUrl }
        ]
      },
      {
        "@type": "CreativeWork",
        "@id": `${pageUrl}#card`,
        name: cardName,
        headline: isSpanish ? `Interpretación de ${cardName} en el tarot` : `${card.name} tarot interpretation`,
        alternativeHeadline: cardSubtitle,
        description: metaDescription,
        url: pageUrl,
        image: absoluteCardImg,
        about: [card.suit ? { "@type": "Thing", name: cardSuit } : undefined].filter(Boolean),
        mentions: [
          ...localizedSymbols.map((symbol) => ({
            "@type": "Thing",
            name: symbol.label,
            description: symbol.meaning || undefined
          })),
          card.loveMeaning ? { "@type": "Thing", name: isSpanish ? `${cardName} en el amor` : `${card.name} love meaning` } : undefined,
          card.careerMeaning ? { "@type": "Thing", name: isSpanish ? `${cardName} en el trabajo` : `${card.name} career meaning` } : undefined,
          card.spiritualMeaning ? { "@type": "Thing", name: isSpanish ? `${cardName} en lo espiritual` : `${card.name} spiritual meaning` } : undefined
        ].filter(Boolean),
        keywords: schemaKeywords,
        isPartOf: { "@id": `${pageUrl}#webpage` },
        inLanguage: isSpanish ? "es" : "en"
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        mainEntity: (pageFaqs.length
          ? pageFaqs
          : [
              {
                question: isSpanish ? `¿Qué significa ${cardName} al derecho?` : `What does ${card.name} mean upright?`,
                answer: uprightDesc0 || (isSpanish ? `Explora las cualidades y la guía de ${cardName} al derecho.` : `Discover the upright qualities and guidance of ${card.name}.`)
              },
              {
                question: isSpanish ? `¿Qué significa ${cardName} invertida?` : `What does ${card.name} mean reversed?`,
                answer: reversedDesc0 || (isSpanish ? `Explora los matices y aprendizajes de ${cardName} invertida.` : `Explore the reversed themes and lessons of ${card.name}.`)
              }
            ]
        ).map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer
          }
        }))
      },
      {
        "@type": "HowTo",
        "@id": `${pageUrl}#howto`,
        name: isSpanish ? `Cómo leer ${cardName} en una tirada de tarot` : `How to read ${card.name} in a tarot spread`,
        description: isSpanish ? `Pasos prácticos para interpretar ${cardName} al derecho e invertida.` : `Practical steps for interpreting ${card.name} using upright and reversed meanings.`,
        step: [
          { "@type": "HowToStep", name: isSpanish ? "Observa la imagen" : "Observe the imagery", text: isSpanish ? "Mira símbolos, postura y ambiente para captar el tono de la carta." : "Notice symbols, posture and setting that hint at the card's tone." },
          { "@type": "HowToStep", name: isSpanish ? "Ponla en contexto" : "Place in context", text: isSpanish ? "Considera el palo, el número y la posición dentro de la tirada." : "Consider the suit, number and spread position to frame the message." },
          { "@type": "HowToStep", name: isSpanish ? "Compara derecho e invertido" : "Check upright vs reversed", text: isSpanish ? "Elige el matiz que mejor responde a la pregunta." : "Read both lists and choose what best fits the question." },
          { "@type": "HowToStep", name: isSpanish ? "Relaciónala con la pregunta" : "Relate to the question", text: isSpanish ? "Lleva las palabras clave a amor, trabajo o crecimiento personal." : "Tie keywords back to love, career or personal growth as needed." },
          { "@type": "HowToStep", name: isSpanish ? "Sintetiza" : "Synthesize", text: isSpanish ? "Resume el mensaje en una frase y nombra un paso concreto." : "Summarize the takeaway in one sentence and note any action step." }
        ],
        inLanguage: isSpanish ? "es" : "en"
      }
    ]
  };

  return (
    <div className="min-h-screen" style={{ background: CARD_BG }}>
      <Seo
        title={pageTitle}
        description={metaDescription}
        url={pageUrl}
        image={absoluteCardImg}
        schema={schemaGraph}
        keywords={schemaKeywords}
      />

      <div className="px-3.5 sm:px-8 md:px-16 py-4 sm:py-6 max-w-6xl mx-auto text-white/95 font-[Cormorant_Garamond]">
        {/* Breadcrumb */}
        <nav
          className="mb-2 sm:mb-3 flex flex-wrap items-center gap-1.5 sm:gap-2 text-[12px] sm:text-[13px] font-semibold uppercase tracking-[0.12em] text-white/70"
          aria-label={copy?.breadcrumb || "Breadcrumb"}
        >
          <Link to={localizedPath("/tarot-cards")} className="transition hover:text-white">
            {copy?.allTarotCards || "All Tarot Cards"}
          </Link>
          {card.suit && (
            <>
              <ChevronRight
                aria-hidden="true"
                className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white/65"
                strokeWidth={2}
              />
              <Link to={localizedPath(suitToPath[card.suit])} className="transition hover:text-white">
                {isSpanish ? cardSuit : `${card.suit} Suit`}
              </Link>
            </>
          )}
        </nav>

        <div className="h-px bg-white/25 mt-2 mb-4 sm:mb-6" />

        {/* Layout */}
        <div className="flex flex-col md:flex-row gap-3 sm:gap-4 md:items-start">
          {/* Mobile Title */}
          <div className="md:hidden order-1 text-center">
            <h1 className="text-[25px] leading-tight font-semibold mb-1 text-white">{displayPageHeading}</h1>
            <h2 className="text-[17px] leading-tight text-white/80 italic mb-0">{cardSubtitle}</h2>
          </div>

          {/* Image */}
          <div className="md:w-[42%] w-full flex justify-center md:justify-end items-start md:pt-1 pt-1 sm:pt-2 order-2 md:order-10">
            <img
              src={cardImg}
              alt={imageAlt}
              className="w-[165px] sm:w-[190px] md:w-[300px] lg:w-[340px] h-auto select-none pointer-events-none rounded-lg"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px bg-white/25" />

          {/* Desktop Title + Description */}
          <div className="md:w-[58%] order-3">
            <div className="hidden md:block">
              <h1 className="text-3xl font-semibold mb-2 text-white">{displayPageHeading}</h1>
              <h2 className="text-lg text-white/80 italic mb-4">{cardSubtitle}</h2>
            </div>
            <p className={`${CONTENT_TEXT} whitespace-pre-line mb-1`}>
              {isSpanish ? spanishCardOverview(card, uprightDesc0, reversedDesc0) : card.description}
            </p>
          </div>
        </div>

        <div className="h-px bg-white/25 my-7 sm:my-10" />

        {card.symbols?.length > 0 && (
          <section>
            <h3 className={`${SECTION_TITLE} mb-4 sm:mb-6 text-center`}>
              {copy?.symbols?.toUpperCase() || "SYMBOLS"}
            </h3>
            <ul className="mx-auto grid max-w-5xl grid-cols-1 gap-x-12 gap-y-4 text-[16.5px] leading-[1.65] sm:grid-cols-2 sm:gap-y-6 sm:text-[17px] sm:leading-[1.7]">
              {localizedSymbols.map((symbol, index) => {
                const { label, meaning } = symbol;
                return (
                  <li
                    key={`${label}-${index}`}
                    className="relative max-w-[34rem] pl-4"
                  >
                    <span
                      aria-hidden="true"
                      className="absolute left-0 top-[0.68em] h-1.5 w-1.5 rounded-full bg-white/45"
                    />
                    <span className="block font-semibold text-[#F7E4B0]">{label}</span>
                    <span className="mt-1 block text-white/90">{meaning}</span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {(card.loveMeaning || card.careerMeaning || card.spiritualMeaning) && (
          <>
            <div className="h-px bg-white/25 my-7 sm:my-10" />
            <section className="mt-4 sm:mt-5">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-[0.08em] mb-5 sm:mb-7 text-white text-center">
                {copy?.realLife?.toUpperCase() || "REAL LIFE"}
              </h2>
              <div className="relative mx-auto max-w-4xl border-l border-white/25 pl-5 sm:pl-8 space-y-5 sm:space-y-7">
                {[
                  {
                    show: card.loveMeaning,
                    title: copy?.love || "Love",
                    text: isSpanish ? spanishLifeMeaning(card, "love") : card.loveMeaning,
                    color: "text-[#F4B8CC]",
                    icon: Heart,
                  },
                  {
                    show: card.careerMeaning,
                    title: copy?.career || "Career",
                    text: isSpanish ? spanishLifeMeaning(card, "career") : card.careerMeaning,
                    color: "text-[#D9C28D]",
                    icon: Briefcase,
                  },
                  {
                    show: card.spiritualMeaning,
                    title: copy?.spirit || "Spirit",
                    text: isSpanish ? spanishLifeMeaning(card, "spirit") : card.spiritualMeaning,
                    color: "text-[#A7D7F3]",
                    icon: Sparkles,
                  },
                ].filter((item) => item.show).map((item, index) => (
                  <section key={item.title} className={`relative ${index > 0 ? "pt-1" : ""}`}>
                    <h3 className="mb-1.5 sm:mb-2 flex items-center gap-2 text-xl font-semibold text-white">
                      <item.icon
                        aria-hidden="true"
                        className={`h-[15px] w-[15px] shrink-0 ${item.color}`}
                        strokeWidth={1.55}
                      />
                      <span>{item.title}</span>
                    </h3>
                    <p className="text-[16.5px] leading-[1.65] sm:text-[17px] sm:leading-[1.8] text-white/90">{item.text}</p>
                  </section>
                ))}
              </div>
            </section>
          </>
        )}

        <div className="h-px bg-white/25 my-7 sm:my-10" />

        {/* Upright vs Reversed (distinct looks) */}
        <div className="mt-4 sm:mt-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
            {/* UPRIGHT - brighter turquoise glass */}
            <div className={`${BOX_BASE} ${UPRIGHT_BG} border-white/40`}>
              <div className={MEANING_HEADER}>
                <ArrowUpFromLine className={`${MEANING_ICON} text-[#A8E7F0]`} aria-hidden="true" />
                <h3 className={PANEL_TITLE}>
                  {copy?.upright?.toUpperCase() || "UPRIGHT MEANING"}
                </h3>
              </div>
              <ul className={MEANING_LIST}>
                {uprightMeanings.map((item, index) => {
                  const [label, description] = splitMeaningText(item);
                  return (
                    <li key={index} className={MEANING_ITEM}>
                      <span className={MEANING_LABEL}>{label}:</span>{" "}
                      {description}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* REVERSED - plum-tinted glass with deep navy fade */}
            <div className={`${BOX_BASE} ${REVERSED_BG} border-white/40`}>
              <div className={MEANING_HEADER}>
                <FlipVertical2 className={`${MEANING_ICON} text-[#F0B3C0]`} aria-hidden="true" />
                <h3 className={PANEL_TITLE}>
                  {copy?.reversed?.toUpperCase() || "REVERSED MEANING"}
                </h3>
              </div>
              <ul className={MEANING_LIST}>
                {reversedMeanings.map((item, index) => {
                  const [label, description] = splitMeaningText(item);
                  return (
                    <li key={index} className={MEANING_ITEM}>
                      <span className={MEANING_LABEL}>{label}:</span>{" "}
                      {description}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        <div className="h-px bg-white/25 my-7 sm:my-10" />

        {/* How to Read */}
        <h3 className={`${SECTION_TITLE} mb-2 sm:mb-3`}>{copy?.howToRead?.toUpperCase() || "HOW TO READ THIS CARD"}</h3>
        <p className={`${CONTENT_TEXT} whitespace-pre-line mb-6 sm:mb-8`}>
          {isSpanish ? spanishCardReading(card) : card.reading}
        </p>

        {(card.shadowMessage || card.journalPrompts?.length > 0) && (
          <section className="mt-8 border-t border-white/25 pt-6 sm:mt-12 sm:pt-8">
            <div
              className={`grid grid-cols-1 gap-5 ${
                hasReflectionPair
                  ? "md:grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)] md:gap-8"
                  : "md:grid-cols-1"
              }`}
            >
              {card.shadowMessage && (
                <section className="py-3 sm:py-5">
                  <div className="mb-3 sm:mb-4 flex items-center justify-between gap-4">
                    <h3 className={`flex items-center gap-2.5 ${SECTION_TITLE}`}>
                      <EyeOff
                        aria-hidden="true"
                        className="h-[16px] w-[16px] shrink-0 text-[#DDB7C6]/75"
                        strokeWidth={1.35}
                      />
                      <span>{copy?.hiddenChallenge?.toUpperCase() || "HIDDEN CHALLENGE"}</span>
                    </h3>
                  </div>
                  <p className={CONTENT_TEXT}>
                    {isSpanish ? spanishShadowMessage(card) : card.shadowMessage}
                  </p>
                </section>
              )}

              {hasReflectionPair && (
                <div className="hidden w-px self-stretch bg-white/25 md:block" aria-hidden="true" />
              )}

              {journalPrompts.length > 0 && (
                <section className="py-3 sm:py-5">
                  <div className="mb-3 sm:mb-4 flex items-center justify-between gap-4">
                    <h3 className={`flex items-center gap-2.5 ${SECTION_TITLE}`}>
                      <PenLine
                        aria-hidden="true"
                        className="h-[16px] w-[16px] shrink-0 text-[#9BD6E8]/75"
                        strokeWidth={1.35}
                      />
                      <span>{copy?.askYourself?.toUpperCase() || "ASK YOURSELF"}</span>
                    </h3>
                  </div>
                  <ul className={`space-y-3 sm:space-y-4 ${CONTENT_TEXT}`}>
                    {journalPrompts.map((prompt) => (
                      <li
                        key={prompt}
                        className="relative pl-4"
                      >
                        <span
                          aria-hidden="true"
                          className="absolute left-0 top-[0.77em] h-1 w-1 rounded-full bg-[#9BD6E8]/70"
                        />
                        <p>{prompt}</p>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </section>
        )}

        {/* Mirroring Cards */}
        {relatedCardSlugs.length > 0 && (
          <section className="mt-7 pt-6 border-t border-white/25 sm:mt-8 sm:pt-8">
            <h3 className={`${SECTION_TITLE} text-center mb-2`}>
              {copy?.cardsToReadWith?.toUpperCase() || "CARDS TO READ WITH"}
            </h3>
            <p className="text-[15px] sm:text-base text-center text-white/85 mb-3 sm:mb-4">
              {isSpanish
                ? "Estas cartas dialogan con el mensaje de esta lectura. Ábrelas para ver más contexto:"
                : "These cards resonate with or contrast this card's themes. Explore them for deeper context:"}
            </p>

            <div className="mt-2 flex flex-wrap justify-center gap-4 sm:gap-6">
              {relatedCardSlugs.map((mirrorSlug) => {
                const mirroredCard = Object.values(cardData).find((c) => c.slug === mirrorSlug);
                if (!mirroredCard) return null;
                return (
                  <Link
                    to={localizedPath(cardPath(mirroredCard))}
                    key={mirrorSlug}
                    className="w-[104px] sm:w-[120px] group transition duration-200 ease-out hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#F7E4B0]"
                  >
                    <div className="rounded-xl overflow-hidden border border-white/15 bg-white/5 backdrop-blur-md transition duration-200 ease-out group-hover:border-[#F7E4B0]/55 group-hover:bg-white/12">
                      <img
                        src={mirroredCard.image}
                        alt={isSpanish ? translateCardAlt(mirroredCard.name) : mirroredCard.imageAlt || `${mirroredCard.name} tarot card`}
                        className="w-full h-auto transition duration-200 ease-out group-hover:scale-[1.025]"
                      />
                    </div>
                    <p className="mt-2 text-center text-sm font-medium text-white/90 transition duration-200 group-hover:text-[#F7E4B0]">
                      {translateCardName(mirroredCard.name)}
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Emotional Themes */}
        {card.emotions?.length > 0 && (
          <>
            <div className="my-6 h-px bg-white/25 sm:my-8" />
            <div className="mt-2">
              <h3 className={`${SECTION_TITLE} mb-3 sm:mb-4`}>
                {copy?.emotionalThemes?.toUpperCase() || "EMOTIONAL THEMES"}
              </h3>
              <p className="text-sm text-white/85 mb-3">
                {isSpanish ? "Esta carta puede aparecer cuando te sientes:" : "This card may appear when you are feeling:"}
              </p>
              <div className="flex gap-2 sm:gap-3 flex-wrap">
                {card.emotions.map((emotion) => (
                  <Link
                    key={emotion}
                    to={localizedPath(`/emotions/${emotion.replace(/\s+/g, "-").toLowerCase()}`)}
                    className="border border-white/45 bg-white/10 text-white/95 text-sm px-3 py-1 rounded-full hover:bg-white/20 transition"
                  >
                    {translateEmotionLabel(emotion.replace(/-/g, " "))}
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="my-6 h-px bg-white/25 sm:my-8" />

        {/* Suit Navigation Buttons */}
        <div className="text-white mb-4">
          <div className="tf-outline-button-row">
            {suitNavItems.map((item) => (
              <Link
                key={item.label}
                to={item.path}
                aria-current={item.active ? "page" : undefined}
                className="tf-outline-button"
              >
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


